/**
 * The EDI ship-date cutoff (`minShipDate`) — shape rules shared by the backend
 * gate and the settings UI that edits it.
 *
 * Lives here rather than in either workspace because both sides must agree on
 * exactly what a usable cutoff is: the UI decides what an operator may save,
 * the backend decides what it will act on, and a value one accepts but the
 * other ignores is the failure this validation exists to prevent (a cutoff that
 * looks set while every document keeps sending).
 *
 * The gate itself — comparing an order's ship date against the cutoff — stays
 * in the backend; only the shape rules are shared.
 */

import { z } from "zod";

/** `YYYY-MM-DD`, the only shape a cutoff may take. */
export const MIN_SHIP_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Whether a shape-valid string is also a real day on the calendar.
 *
 * The shape alone is not enough, because the cutoff is compared
 * lexicographically: `"2026-99-99"` sorts above every real ship date and would
 * withhold **every** automated 810/856/880 for that customer, silently and
 * indefinitely. `"2026-00-00"` sorts below every date and makes the cutoff
 * inert. Both look like a working cutoff to whoever typed them.
 *
 * Arithmetic rather than `new Date()`: no timezone may influence a date
 * decision here, and a parser would roll `2026-02-30` over to March 2nd instead
 * of refusing it.
 */
export function isRealCalendarDate(value: string): boolean {
  if (!MIN_SHIP_DATE_PATTERN.test(value)) return false;

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));

  if (month < 1 || month > 12 || day < 1) return false;

  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const maxDay =
    month === 2 && isLeapYear ? 29 : (DAYS_IN_MONTH[month - 1] as number);

  return day <= maxDay;
}

/**
 * Field definition for the 810 / 856 / 880 document config schemas. Optional
 * and, when present, an empty string is treated as "not set" so clearing the
 * field in the settings UI reliably turns the gate off.
 */
export const MinShipDateSchema = z
  .string()
  .refine((value) => value === "" || isRealCalendarDate(value), {
    message: "minShipDate must be a real ISO date (YYYY-MM-DD)",
  })
  .optional();
